import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  X,
  Check,
  User,
  CreditCard,
  Shield,
  Car,
  MapPin,
  Camera,
} from "lucide-react";

import { toast } from "sonner";
import KycStepContent from "./KycStepForms";

// ============================================================
// STEP CONFIGURATION
// ============================================================

const STEPS = [
  { id: 1, label: "Address", icon: MapPin },
  { id: 2, label: "Profile Picture", icon: Camera },
  { id: 3, label: "Aadhaar Card", icon: CreditCard },
  { id: 4, label: "PAN Card", icon: Shield },
  { id: 5, label: "Driving License", icon: Car },
  { id: 6, label: "Vehicle Details", icon: User },
];

const DECORATIONS = [
  { left: "4%", top: "12%", size: 33 },
  { left: "15%", top: "75%", size: 20 },
  { left: "30%", top: "15%", size: 39 },
  { left: "48%", top: "83%", size: 28 },
  { left: "61%", top: "7%", size: 22 },
  { left: "78%", top: "68%", size: 35 },
  { left: "90%", top: "13%", size: 26 },
];

// ============================================================
// REUSABLE HELPERS
// ============================================================

const asText = (value) => String(value ?? "").trim();

const asImageUrl = (value) => (typeof value === "string" ? value : null);

// Extract image URL from existing upload API response.
function getUploadedImageUrl(response) {
  if (typeof response === "string") {
    return response.trim();
  }

  const sources = [
    response,
    response?.data,
    response?.response?.[0],
    response?.data?.response?.[0],
    response?.data?.data,
    response?.data?.[0],
  ];

  const keys = ["imageUrl", "url", "fileUrl", "file_path", "path", "image"];

  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    for (const key of keys) {
      const value = source[key];

      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }

  return "";
}

// Upload only new files. Reuse URL for existing images.
async function uploadKycImage(value, driverCode, field, uploadImageToServer) {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }

  if (typeof File === "undefined" || !(value instanceof File)) {
    throw new Error(
      `Please select a valid ${field.replaceAll("_", " ")} image`,
    );
  }

  const response = await uploadImageToServer(value, driverCode, field);

  const url = getUploadedImageUrl(response);

  if (!url) {
    throw new Error(`${field.replaceAll("_", " ")} image URL not received`);
  }

  return url;
}

// Build previous step data in the same structure as existing API.
function buildStepPayload(form, stepNumber) {
  const payload = {
    userid: asText(form.driver_code),
  };

  if (stepNumber >= 3) {
    payload.step1 = {
      city: asText(form.city),
      area: asText(form.area),
    };

    // Original Step 3 payload includes pincode ID.
    if (stepNumber === 3) {
      payload.step1.pinCode = asText(form.pinCodeId);
    }

    payload.step2 = {
      profilePicture: asImageUrl(form.profile_photo),
    };

    payload.step3 = {
      aadharNumber: asText(form.aadhaar_no),
      aadharFrontPhoto: asImageUrl(form.aadhaar_front),
      aadharBackPhoto: asImageUrl(form.aadhaar_back),
    };
  }

  if (stepNumber >= 4) {
    payload.step4 = {
      panCard: asText(form.pan_no).toUpperCase(),
      panCardFrontPhoto: asImageUrl(form.pan_front),
    };
  }

  if (stepNumber >= 5) {
    payload.step5 = {
      drivingLicenseNumber: asText(form.driving_licence).toUpperCase(),
      drivingLicenseFrontPhoto: asImageUrl(form.driving_licence_img),
    };
  }

  return payload;
}

// ============================================================
// MAIN KYC MODAL
// ============================================================

export default function EditKycModal({
  editKycModalOpen,
  setEditKycModalOpen,
  editKycDriver,
  setEditKycDriver,
  currentStep,
  setCurrentStep,
  kycFormdata,
  setKycFormData,
  drivers,
  handleDriverSelect,
  handleFileChange,
  handleStepSubmit,
  getPincodeDetails,
  uploadImageToServer,
  resetForm,
  Loading,
  onSuccess,
  activeTab,
  FetchDriverKycDetails,
  AdminId,
}) {
  const [submitting, setSubmitting] = useState(false);

  const savingRef = useRef(false);
  const completionTimerRef = useRef(null);

  // ============================================================
  // CLOSE MODAL
  // ============================================================

  const handleClose = useCallback(() => {
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }

    setEditKycModalOpen(false);
    setEditKycDriver(null);
    setCurrentStep(1);
    resetForm();
  }, [setEditKycModalOpen, setEditKycDriver, setCurrentStep, resetForm]);

  // ============================================================
  // FINAL COMPLETION
  // ============================================================

  const onCompleted = useCallback(() => {
    handleClose();

    if (activeTab === "Add Kyc Data" || activeTab === "Kyc Details") {
      FetchDriverKycDetails?.();
    }

    onSuccess?.();
  }, [handleClose, activeTab, FetchDriverKycDetails, onSuccess]);

  // ============================================================
  // TIMER CLEANUP
  // ============================================================

  useEffect(() => {
    if (!editKycModalOpen && completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
  }, [editKycModalOpen]);

  useEffect(() => {
    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
      }
    };
  }, []);

  // ============================================================
  // ALL SIX STEP SUBMISSION LOGIC
  // ============================================================

  const submitStep = useCallback(
    async (stepNumber) => {
      if (savingRef.current || Loading) return false;

      savingRef.current = true;
      setSubmitting(true);

      try {
        const driverCode = asText(kycFormdata.driver_code);

        if (!driverCode) {
          toast.error("Please select a driver");
          return false;
        }

        const payload = buildStepPayload(kycFormdata, stepNumber);

        let updates = {};

        switch (stepNumber) {
          // ====================================================
          // STEP 1 - ADDRESS
          // ====================================================

          case 1: {
            const city = asText(kycFormdata.city);
            const area = asText(kycFormdata.area);
            const pincode = asText(kycFormdata.pinCode);
            const pincodeId = asText(kycFormdata.pinCodeId);

            if (!city || !area || !pincode || !pincodeId) {
              toast.error("Please fill in all required fields");
              return false;
            }

            payload.step1 = {
              city,
              area,
              pinCode: pincodeId,
            };

            break;
          }

          // ====================================================
          // STEP 2 - PROFILE PICTURE
          // ====================================================

          case 2: {
            if (!kycFormdata.profile_photo) {
              toast.error("Please upload profile photo");
              return false;
            }

            const imageUrl = await uploadKycImage(
              kycFormdata.profile_photo,
              driverCode,
              "profile_photo",
              uploadImageToServer,
            );

            updates.profile_photo = imageUrl;

            payload.step2 = {
              profilePicture: imageUrl,
            };

            break;
          }

          // ====================================================
          // STEP 3 - AADHAAR
          // ====================================================

          case 3: {
            const aadhaarNumber = asText(kycFormdata.aadhaar_no);

            if (!aadhaarNumber) {
              toast.error("Please enter Aadhaar number");
              return false;
            }

            if (!kycFormdata.aadhaar_front) {
              toast.error("Please upload Aadhaar front image");
              return false;
            }

            if (!kycFormdata.aadhaar_back) {
              toast.error("Please upload Aadhaar back image");
              return false;
            }

            const frontUrl = await uploadKycImage(
              kycFormdata.aadhaar_front,
              driverCode,
              "aadhaar_front",
              uploadImageToServer,
            );

            const backUrl = await uploadKycImage(
              kycFormdata.aadhaar_back,
              driverCode,
              "aadhaar_back",
              uploadImageToServer,
            );

            updates = {
              aadhaar_no: aadhaarNumber,
              aadhaar_front: frontUrl,
              aadhaar_back: backUrl,
            };

            payload.step3 = {
              aadharNumber: aadhaarNumber,
              aadharFrontPhoto: frontUrl,
              aadharBackPhoto: backUrl,
            };

            break;
          }

          // ====================================================
          // STEP 4 - PAN CARD
          // ====================================================

          case 4: {
            const panNumber = asText(kycFormdata.pan_no).toUpperCase();

            if (!panNumber) {
              toast.error("Please enter PAN card number");
              return false;
            }

            if (panNumber.length !== 10) {
              toast.error("PAN card number must be 10 characters");
              return false;
            }

            if (!kycFormdata.pan_front) {
              toast.error("Please upload PAN card image");
              return false;
            }

            const imageUrl = await uploadKycImage(
              kycFormdata.pan_front,
              driverCode,
              "pan_front",
              uploadImageToServer,
            );

            updates = {
              pan_no: panNumber,
              pan_front: imageUrl,
            };

            payload.step4 = {
              panCard: panNumber,
              panCardFrontPhoto: imageUrl,
            };

            break;
          }

          // ====================================================
          // STEP 5 - DRIVING LICENCE
          // ====================================================

          case 5: {
            const licenceNumber = asText(
              kycFormdata.driving_licence,
            ).toUpperCase();

            if (!licenceNumber) {
              toast.error("Please enter driving licence number");
              return false;
            }

            if (!kycFormdata.driving_licence_img) {
              toast.error("Please upload driving licence image");
              return false;
            }

            const imageUrl = await uploadKycImage(
              kycFormdata.driving_licence_img,
              driverCode,
              "driving_licence_img",
              uploadImageToServer,
            );

            updates = {
              driving_licence: licenceNumber,
              driving_licence_img: imageUrl,
            };

            payload.step5 = {
              drivingLicenseNumber: licenceNumber,
              drivingLicenseFrontPhoto: imageUrl,
            };

            break;
          }

          // ====================================================
          // STEP 6 - VEHICLE DETAILS
          // ====================================================

          case 6: {
            const licenceNumber = asText(
              kycFormdata.driving_licence,
            ).toUpperCase();

            const description = asText(kycFormdata.transport_description);

            const licencePlate = asText(
              kycFormdata.licence_plate,
            ).toUpperCase();

            if (!licenceNumber) {
              toast.error("Driving license number is required");
              return false;
            }

            if (!description) {
              toast.error("Transport description is required");
              return false;
            }

            if (!licencePlate) {
              toast.error("License plate number is required");
              return false;
            }

            if (!kycFormdata.vehicle_name_plate_img) {
              toast.error("Please upload license plate image");
              return false;
            }

            const imageUrl = await uploadKycImage(
              kycFormdata.vehicle_name_plate_img,
              driverCode,
              "vehicle_name_plate_img",
              uploadImageToServer,
            );

            updates = {
              driving_licence: licenceNumber,
              licence_plate: licencePlate,
              vehicle_name_plate_img: imageUrl,
            };

            payload.step6 = {
              transportTypeID: licenceNumber,
              transportDescription: description,
              licensePlate: licencePlate,
              licensePlatePhoto: imageUrl,
            };

            break;
          }

          default:
            return false;
        }

        // ====================================================
        // SAVE UPLOADED URLS TO FORM STATE
        // ====================================================

        if (Object.keys(updates).length > 0) {
          setKycFormData((previous) => ({
            ...previous,
            ...updates,
          }));
        }

        // ====================================================
        // CALL EXISTING API HANDLER
        // ====================================================

        const success = await handleStepSubmit(stepNumber, payload);

        if (!success) return false;

        // ====================================================
        // MOVE TO NEXT STEP
        // ====================================================

        if (stepNumber < 6) {
          setCurrentStep(stepNumber + 1);
        } else {
          toast.success("All KYC steps completed successfully!");

          completionTimerRef.current = setTimeout(() => {
            completionTimerRef.current = null;
            onCompleted();
          }, 1500);
        }

        return true;
      } catch (error) {
        console.error(`KYC Step ${stepNumber} error:`, error);

        toast.error(
          error?.response?.data?.responseMessage ||
            error?.message ||
            "Unable to save KYC details",
        );

        return false;
      } finally {
        savingRef.current = false;
        setSubmitting(false);
      }
    },
    [
      kycFormdata,
      handleStepSubmit,
      uploadImageToServer,
      setKycFormData,
      setCurrentStep,
      onCompleted,
      Loading,
    ],
  );

  if (!editKycModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
      <div className="relative max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl sm:p-6">
        {/* HEADER */}
        <div className="sticky top-0 z-20 mb-4 flex items-center justify-between border-b bg-white pb-4">
          <h2 className="text-xl font-semibold text-gray-900">Edit KYC Data</h2>

          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100"
            aria-label="Close KYC modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* SIX STEP PROGRESS HEADER */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 px-4 py-6 sm:px-8 sm:py-8">
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden="true"
          >
            {DECORATIONS.map((item, i) => (
              <span
                key={i}
                className="absolute rounded-full bg-white/35"
                style={{
                  left: item.left,
                  top: item.top,
                  width: item.size,
                  height: item.size,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 flex items-start justify-between gap-3 overflow-x-auto pb-2 sm:justify-center sm:gap-7">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const completed = currentStep > step.id;
              const current = currentStep === step.id;

              return (
                <div
                  key={step.id}
                  className="flex min-w-[95px] flex-col items-center gap-3 text-center"
                >
                  <div
                    className={`relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full shadow-md transition-transform sm:h-14 sm:w-14 ${
                      completed
                        ? "bg-green-400 text-white"
                        : current
                          ? "scale-110 bg-orange-400 text-white"
                          : "bg-white text-gray-400"
                    }`}
                  >
                    {current && (
                      <span className="absolute inset-0 animate-ping rounded-full bg-orange-300/20" />
                    )}

                    {completed ? <Check size={22} /> : <Icon size={22} />}
                  </div>

                  <span
                    className={`text-xs font-medium sm:text-sm ${
                      completed
                        ? "text-green-600"
                        : current
                          ? "text-orange-600"
                          : "text-gray-500"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* DYNAMIC STEP CONTENT */}
        <KycStepContent
          currentStep={currentStep}
          kycFormdata={kycFormdata}
          setKycFormData={setKycFormData}
          drivers={drivers}
          editKycDriver={editKycDriver}
          handleDriverSelect={handleDriverSelect}
          handleFileChange={handleFileChange}
          getPincodeDetails={getPincodeDetails}
          submitStep={submitStep}
          setCurrentStep={setCurrentStep}
          loading={Boolean(Loading || submitting)}
        />
      </div>
    </div>
  );
}
